/**
 * Near-duplicate note titles, so a card never proposes "Active customers definition" beside an existing
 * "Active customer" note, a pending proposal or a card already shown. Pure, no I/O, and no import but
 * `context-title`, so the SPA shares it through `@server/title-similarity` (a follow-up chip that repeats
 * an assist card is hidden by it).
 */
import { normalizeContextTitle } from '../context-title.js';

/** Words that say nothing about which term a title names. */
const STOP_WORDS = new Set([
  'a',
  'an',
  'and',
  'are',
  'as',
  'by',
  'definition',
  'definitions',
  'define',
  'defined',
  'for',
  'how',
  'in',
  'is',
  'meaning',
  'means',
  'note',
  'notes',
  'of',
  'on',
  'or',
  'our',
  'the',
  'to',
  'we',
  'what',
  'with',
]);

/** A plural's singular, for the common English endings ("customers" → "customer", "entries" → "entry"). */
function singular(word: string): string {
  if (word.length > 4 && word.endsWith('ies')) return `${word.slice(0, -3)}y`;
  if (word.length > 3 && word.endsWith('s') && !word.endsWith('ss')) return word.slice(0, -1);
  return word;
}

/** The words a title names: normalized (`normalizeContextTitle`), stop words dropped, plurals singular. */
export function titleTokens(title: string): Set<string> {
  const words = normalizeContextTitle(title.replace(/[_-]+/g, ' ')).split(' ');
  return new Set(words.filter((word) => word.length > 0 && !STOP_WORDS.has(word)).map(singular));
}

/** Token-set Jaccard similarity of two titles; two titles with no words compare by their text. */
export function titleSimilarity(a: string, b: string): number {
  const left = titleTokens(a);
  const right = titleTokens(b);
  if (left.size === 0 || right.size === 0) {
    return normalizeContextTitle(a) === normalizeContextTitle(b) ? 1 : 0;
  }
  let shared = 0;
  for (const word of left) if (right.has(word)) shared += 1;
  return shared / (left.size + right.size - shared);
}

/** Two titles at or above this similarity name the same thing. */
export const NEAR_DUPLICATE_THRESHOLD = 0.6;

/** True when `title` is a near-duplicate of any of `others`. */
export function isNearDuplicateTitle(title: string, others: readonly string[]): boolean {
  return others.some((other) => titleSimilarity(title, other) >= NEAR_DUPLICATE_THRESHOLD);
}
